import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicSavingsInvestments from "app/components/Individual/applicant/assetsLiabilities/DynamicSavingsInvestments";
import DynamicSavingsOtherAssets from "app/components/Individual/applicant/assetsLiabilities/DynamicSavingsOtherAssets";
import { handleApiError } from "app/utils/handleApiError";

export interface AssetsLiabilitiesPartTwoFormData {
  savingAndInvestmentsType: string;
  otherAssetsType: string;
  savingAndInvestments: { dbId?: string; detail: string; value: string }[];
  otherAssets: { dbId?: string; detail: string; value: string }[];
}

const PartTwo: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const { mutate: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formTwo, assetLiabilityId, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  ) || {
    formTwo: { savingAndInvestments: [], otherAssets: [] },
  };
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<AssetsLiabilitiesPartTwoFormData>({
    defaultValues: {
      savingAndInvestments: [{ detail: "", value: "" }],
      otherAssets: [{ detail: "", value: "" }],
      savingAndInvestmentsType: "no",
      otherAssetsType: "no",
    },
  });
  const savingsinvestmentsValue = watch("savingAndInvestmentsType");
  const otherassetsValue = watch("otherAssetsType");

  const onSubmit = (data: AssetsLiabilitiesPartTwoFormData) => {
    if (!savedIds?.applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const {
      savingAndInvestments,
      savingAndInvestmentsType,
      otherAssetsType,
      otherAssets,
    } = data || {};

    const payload: any = {};
    if (savingAndInvestmentsType === "yes") {
      payload.savingAndInvestments = savingAndInvestments.map((item) => ({
        detail: item.detail,
        ...(item.dbId && { id: item.dbId }),
        value: Number(item.value),
      }));
    } else {
      payload.savingAndInvestments = null;
    }
    if (otherAssetsType === "yes") {
      payload.otherAssets = otherAssets.map((item) => ({
        detail: item.detail,
        ...(item.dbId && { id: item.dbId }),
        value: Number(item.value),
      }));
    } else {
      payload.otherAssets = null;
    }

    submitAssetsLiabilities(
      {
        url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/second`,
        data: payload,
      },
      {
        onSuccess: (data: any) => {
          const { otherAssets, savingAndInvestments } = data || {};
          const otherAssetsMapped = otherAssets?.map((item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: Number(item.value),
          }));
          const savingAndInvestmentsMapped = savingAndInvestments?.map(
            (item: any) => ({
              dbId: item.id,
              detail: item.detail,
              value: Number(item.value),
            })
          );

          dispatch(
            addStepsData({
              ...others,
              formTwo: {
                ...formTwo,
                savingAndInvestments:
                  savingAndInvestmentsType === "no"
                    ? []
                    : savingAndInvestmentsMapped,
                otherAssets: otherAssetsType === "no" ? [] : otherAssetsMapped,
                otherAssetsType,
                savingAndInvestmentsType,
              },
            })
          );
          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "savingAndInvestments",
  });

  const {
    fields: superannuationFields,
    append: superannuationAppend,
    remove: superannuationRemove,
  } = useFieldArray({
    control,
    name: "otherAssets",
  });

  useEffect(() => {
    if (formTwo && Object.keys(formTwo).length > 0) {
      reset({
        ...(formTwo?.otherAssetsType && {
          otherAssetsType: formTwo?.otherAssetsType,
        }),
        ...(formTwo?.savingAndInvestmentsType && {
          savingAndInvestmentsType: formTwo?.savingAndInvestmentsType,
        }),
        savingAndInvestments: formTwo?.savingAndInvestments?.length
          ? formTwo.savingAndInvestments
          : [{ detail: "", value: "" }],
        otherAssets: formTwo?.otherAssets?.length
          ? formTwo.otherAssets
          : [{ detail: "", value: "" }],
      });
    }
  }, [formTwo, reset]);

  return (
    <>
      <ProgressBar progress={12} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Saving & Investments/Other Assets" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any savings & investments?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="savingAndInvestmentsType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="savingsinvestments_yes"
                      name="savingAndInvestmentsType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="savingsinvestments_no"
                      name="savingAndInvestmentsType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.savingAndInvestmentsType && (
              <ErrorsMessage
                title={errors.savingAndInvestmentsType.message}
                className="!text-left"
              />
            )}
          </div>

          {savingsinvestmentsValue === "yes" && (
            <>
              <DynamicSavingsInvestments
                control={control}
                errors={errors}
                fields={fields}
                deleteItem={deleteItem}
                savingAndInvestments="savingAndInvestments"
                remove={remove}
                applicantID={savedIds?.applicantID as string}
                deleteIsPending={deleteIsPending}
              />

              {fields.length < 3 && (
                <div className="flex items-center justify-end mt-6  mb-4 sm:mb-2">
                  <AddMore onClick={() => append({ detail: "", value: "" })} />
                </div>
              )}
            </>
          )}

          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any other assets?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="otherAssetsType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="otherassets_yes"
                      name="otherAssetsType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="otherassets_no"
                      name="otherAssetsType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.otherAssetsType && (
              <ErrorsMessage
                title={errors.otherAssetsType.message}
                className="!text-left"
              />
            )}
          </div>

          {otherassetsValue === "yes" && (
            <>
              <DynamicSavingsOtherAssets
                control={control}
                errors={errors}
                fields={superannuationFields}
                deleteItem={deleteItem}
                otherAssets="otherAssets"
                remove={superannuationRemove}
                applicantID={savedIds?.applicantID as string}
                deleteIsPending={deleteIsPending}
              />
              {superannuationFields.length < 3 && (
                <div className="flex items-center justify-end mt-6">
                  <AddMore
                    onClick={() =>
                      superannuationAppend({
                        detail: "",
                        value: "",
                      })
                    }
                  />
                </div>
              )}
            </>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartTwo;
